import type { ReactNode } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { AppShell, useIsWide } from '@/ui'
import Login from './routes/Login'
import Outreach from './routes/outreach/Campaigns'
import OutreachDiscovery from './routes/outreach/Discovery'
import OutreachQueue from './routes/outreach/Queue'
import OutreachProspect from './routes/outreach/Prospect'
import OutreachAnalytics from './routes/outreach/Analytics'
import { isRedirected, redirectRoutes } from './routes/_redirects'
import AccountList from './routes/accounts/AccountList'
import AccountDetail from './routes/accounts/AccountDetail'
import CampaignList from './routes/campaigns/CampaignList'
import CampaignDetail from './routes/campaigns/CampaignDetail'
import NewOutreach from './routes/creators/NewOutreach'
import OutreachList from './routes/creators/OutreachList'
import OutreachPage from './routes/creators/OutreachPage'
import PoolList from './routes/creators/PoolList'
import PoolPage from './routes/creators/PoolPage'
import Media from './routes/media/Media'
import Jobs from './routes/system/Jobs'
import Settings from './routes/system/Settings'
import Today from './routes/today/Today'
import Review from './routes/review/Review'
import Results from './routes/results/Results'
import Affiliate from './routes/affiliate/Affiliate'
import RedeemList from './routes/redeem/RedeemList'
import AppWorkspace from './routes/redeem/AppWorkspace'
import Requests from './routes/redeem/Requests'
import Desk from './routes/redeem/Desk'
import ScheduleList from './routes/schedule/ScheduleList'
import ScheduleCalendar from './routes/schedule/ScheduleCalendar'
import { DraftRoute, RedeemPostRoute } from './routes/schedule/PostDrawer'
import type { DetailState } from './routes/schedule/shared'

/** Pages of the new layout that are built. */
const NEW_ROUTES: { path: string; element: ReactNode }[] = [
  { path: '/', element: <Today /> },
  { path: '/review', element: <Review /> },
  { path: '/results', element: <Results /> },
  { path: '/schedule', element: <ScheduleList /> },
  { path: '/schedule/calendar', element: <ScheduleCalendar /> },
  // A post's detail. Opened from a list it is drawn over that list (see
  // DETAIL_ROUTES); opened by its address it brings the list itself.
  { path: '/drafts/:id', element: <DraftRoute /> },
  { path: '/redeem/posts/:id', element: <RedeemPostRoute /> },
  { path: '/redeem', element: <RedeemList /> },
  { path: '/redeem/requests', element: <Requests /> },
  { path: '/redeem/desk', element: <Desk /> },
  // An app's workspace: 개요, 코드, 요청, 글, 설정 are its own nested routes.
  { path: '/redeem/:appId/*', element: <AppWorkspace /> },
  { path: '/accounts', element: <AccountList /> },
  { path: '/accounts/:id', element: <AccountDetail /> },
  { path: '/affiliate', element: <Affiliate /> },
  { path: '/campaigns', element: <CampaignList /> },
  { path: '/campaigns/:id', element: <CampaignDetail /> },
  { path: '/creators', element: <PoolList /> },
  { path: '/creators/outreaches', element: <OutreachList /> },
  { path: '/creators/outreaches/new', element: <NewOutreach /> },
  { path: '/creators/outreaches/:id', element: <OutreachPage /> },
  { path: '/creators/:id', element: <PoolPage /> },
  { path: '/media', element: <Media /> },
  { path: '/jobs', element: <Jobs /> },
  { path: '/settings', element: <Settings /> },
]

/** The details drawn over the page they were opened from, from 1024px up. */
const DETAIL_ROUTES: { path: string; element: ReactNode }[] = [
  { path: '/drafts/:id', element: <DraftRoute overlay /> },
  { path: '/redeem/posts/:id', element: <RedeemPostRoute overlay /> },
]

/** The pages from before the redesign, mounted unchanged inside the new shell
 *  (strangler). Each Phase moves some to new pages and deletes them here; an
 *  old path whose redirect is active is no longer mounted. */
const LEGACY_ROUTES: { path: string; element: ReactNode }[] = [
  { path: '/outreach', element: <Outreach /> },
  { path: '/outreach/:campaignId/discovery', element: <OutreachDiscovery /> },
  { path: '/outreach/:campaignId/queue', element: <OutreachQueue /> },
  { path: '/outreach/:campaignId/prospects/:prospectId', element: <OutreachProspect /> },
  { path: '/outreach/:campaignId/analytics', element: <OutreachAnalytics /> },
]

export const MOUNTED_PATHS = [
  ...NEW_ROUTES.map((r) => r.path),
  ...LEGACY_ROUTES.filter((r) => !isRedirected(r.path)).map((r) => r.path),
]

export default function App() {
  const location = useLocation()
  const wide = useIsWide()
  // A detail opened from a page carries that page as its background: on a
  // wide screen the page stays drawn (and keeps its filters and scroll) with
  // the detail as a drawer on top. Below 1024px the detail is a page.
  const background = wide ? (location.state as DetailState | null)?.background : undefined
  return (
    <>
      <Routes location={background ?? location}>
        <Route path="/login" element={<Login />} />
        <Route element={<AppShell />}>
          {NEW_ROUTES.map((r) => (
            <Route key={r.path} path={r.path} element={r.element} />
          ))}
          {LEGACY_ROUTES.filter((r) => !isRedirected(r.path)).map((r) => (
            <Route key={r.path} path={r.path} element={r.element} />
          ))}
          {redirectRoutes()}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
      {background && (
        <Routes>
          {DETAIL_ROUTES.map((r) => (
            <Route key={r.path} path={r.path} element={r.element} />
          ))}
          <Route path="*" element={null} />
        </Routes>
      )}
    </>
  )
}
